import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import type * as admin from '../admin'
import type * as common from '../common'
import type * as display from '../display'
import * as dms from '../dms'

import type { Widget } from './widget'

/**
 * One of the most central concepts in the BI application is a
 * {@link Page}. This is primarily a collection of {@link Widget} in a
 * particular layout (i.e. {@link Page.content})
 */
export interface Page
  extends admin.Page,
    common.Identified<Page.Id>,
    common.Aliased {
  // TODO: maybe `ref: string`, represents a string given by the user to identify
  // this dashboard in links (i.e. instead of using an auto-generated id, you
  // can use a known, readable value; sort of like a slug for a webpage)
  /**
   * Contains query that can retrieve additional rendering
   * {@link Page.Query.Result data} for the page
   */
  query?: Page.Query
  /**
   * Contains all of the {@link Widget} to display in this {@link Page}
   *
   * At the top-level, this is a single {@link Page.Column} which can
   * contain nested {@link Page.Row}, {@link Page.Column}, and of
   * course, {@link Widget}
   */
  // NOTE: we start with a column because that ensures we get a set height to
  // our content
  content: Page.Content
  /**
   * Represents all of the {@link dms.data.query.Variable} that the user can input for
   * this {@link Page}
   */
  variables: readonly dms.data.query.Variable[]
}
export namespace Page {
  /**
   * Identifies a {@link Page}
   */
  export type Id = string
  export namespace Id {
    export const isId = TypeChecker.isString.usingAlias('mosaic.Page.Id')
    export const assertIsId = TypeAsserter.Factory.from(isId)
  }

  /**
   * Retrieves additional rendering {@link Query.Result data} for the page
   */
  export type Query = dms.data.query.ForSingleton<Query.Result | undefined>
  export namespace Query {
    /**
     * Return type of a {@link Page.Query}
     *
     * @see {@link Page.Rendering}
     */
    export type Result = Page.Rendering
  }

  /**
   * Represents dynamic {@link Page} information produced by the
   * {@link Page.Query}
   */
  export interface Rendering {
    // NOTE: everything is optional so that we are forced to handle it
    title?: string
    breadcrumbs?: readonly display.Breadcrumb[]
  }

  /**
   * Represents all of the content on a {@link Page}
   *
   * This can either be a single {@link Content.Container} or a list of
   * {@link Segment segments}
   */
  export type Content = MutuallyExclusive<
    [Content.WithSegments, Content.WithoutSegments]
  >
  export namespace Content {
    /**
     * Contains multiple {@link Page.Segment} and a
     * {@link Page.Segment.Navigator}
     */
    export interface WithSegments {
      navigator: Page.Segment.Navigator
      segments: readonly Page.Segment[]
    }

    /**
     * Just a {@link Content.Container}
     */
    export type WithoutSegments = Container

    /**
     * Contains {@link Widget widgets} in a particular layout
     */
    export interface Container {
      /**
       * A layout always starts with a single column which spans the entire
       * width of the page
       */
      root: Column
    }
  }

  /**
   * Represents a portion of a {@link Page}
   *
   * How this portion is surfaced depends on the associated
   * {@link Segment.Navigator}
   */
  export interface Segment
    extends common.Aliased,
      common.Named,
      Content.Container {
    // TODO: cleanup documentation
    /**
     * When specified this {@link Segment} will have a {@link Segment.Rendering}
     * properties asynchronously retrieved utilizing the DMS query
     */
    query?: Segment.Query
  }
  export namespace Segment {
    /**
     * Represents the additional {@link Segment} information
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export interface Rendering
      extends common.Hideable,
        common.Named.Optionally {
      /**
       * Additional bread crumbs that are appended to {@link Page.Rendering.breadcrumbs}
       *
       * @see {@link display.Breadcrumb}
       */
      breadcrumbs?: readonly display.Breadcrumb[]
    }

    /**
     * Describes how each {@link Segment} is presented and navigated to
     */
    export enum Navigator {
      /**
       * One segment is presented at a time, and tabs are used to switch segments
       */
      Tabs = 'tabs',
      /**
       * Segments are vertically stacked, and the navigator can be used to scroll down or up to the desired segment
       */
      Scrolling = 'scrolling',
    }
    // Needs to be before namespace is defined
    const NAVIGATORS: ReadonlySet<Navigator> = new Set(
      Object.values(Navigator) as Navigator[],
    )
    export namespace Navigator {
      export const ALL: readonly Navigator[] = Array.from(NAVIGATORS)
      export const isNavigator = TypeChecker.Factory.oneOf(NAVIGATORS)
      export const assertIsNavigator: TypeAsserter<Navigator> =
        TypeAsserter.Factory.from(isNavigator)
    }

    /**
     * Retrieves additional rendering {@link Query.Result data} for the page
     * segment
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export type Query = dms.data.query.ForSingleton<Query.Result>
    export namespace Query {
      export const isQuery = dms.data.query.Query.isQuery as TypeChecker<Query>
      export const assertIsQuery: TypeAsserter<Query> =
        TypeAsserter.Factory.from(isQuery)
      /**
       * Return type of a {@link Segment.Query}
       *
       * @see {@link Segment.Rendering}
       */
      export type Result = Segment.Rendering
    }
  }

  /**
   * The {@link Widget widgets} in a {@link Page} are laid out in
   * recursively nested {@link Column columns} and {@link Row rows}
   *
   * #### Example
   *
   * #### Structure
   * - Column
   *   - Widget 1 (height = 500)
   *   - Row (height = 500)
   *     - Widget 2 (span = 3)
   *     - Widget 3 (span = 3)
   *     - Column (span = 6)
   *       - Widget 4 (height = 250)
   *       - Row (height = 250)
   *         - Widget 5 (span = 3)
   *         - Widget 6 (span = 3)
   *
   * #### Rendering
   * ```txt
   * --------------------------------------------------------------------------
   * |                                                                        |
   * |                                                                        |
   * |                                                                        |
   * |                                                                        |
   * |                                                                        |
   * |                                Widget 1                                |
   * |                                                                        |
   * |                                                                        |
   * |                                                                        |
   * |                                                                        |
   * |                                                                        |
   * |------------------------------------------------------------------------|
   * |                 |                 |                                    |
   * |                 |                 |                                    |
   * |                 |                 |              Widget 4              |
   * |                 |                 |                                    |
   * |                 |                 |                                    |
   * |    Widget 2     |    Widget 3     |------------------------------------|
   * |                 |                 |                 |                  |
   * |                 |                 |                 |                  |
   * |                 |                 |    Widget 5     |     Widget 6     |
   * |                 |                 |                 |                  |
   * |                 |                 |                 |                  |
   * --------------------------------------------------------------------------
   * ```
   */
  export interface Column {
    /**
     * A portion of this {@link Column}
     *
     * @see {@link Column.Element}
     */
    elements: readonly Column.Element[]
  }
  export namespace Column {
    /**
     * Represents a portion of a {@link Column}. This portion can be a single
     * {@link Widget} or a {@link Row} containing multiple {@link Widget} or
     * nested {@link Column}
     */
    export interface Element {
      /**
       * What should be displayed in this portion of the {@link Column}
       *
       * @see {@link Widget}, {@link Row}
       */
      content: ExactlyOneOf<{ widget: Widget.Ref; row: Row }>
      /**
       * Height of this {@link Element}, generally in pixels
       */
      height: number
    }
  }

  /**
   * @see {@link Column} (contains a full example)
   */
  export interface Row {
    /**
     * A portion of this {@link Row}
     *
     * @see {@link Row.Element}
     */
    elements: readonly Row.Element[]
  }
  export namespace Row {
    /**
     * Represents a portion of a {@link Row}. This portion can be a single
     * {@link Widget} or a {@link Column} containing multiple {@link Widget} or
     * nested {@link Row}
     */
    export interface Element {
      /**
       * What should be displayed in this portion of the {@link Row}
       *
       * @see {@link Widget}, {@link Column}
       */
      content: ExactlyOneOf<{ widget: Widget.Ref; column: Column }>
      /**
       * Determines the width of this {@link Element} using the following formula:
       * > _Element Width_ = _Page Width_ * (_Element Span_ / 12)
       *
       * Generally, the {@link span} should be one of: `3`, `4`, `6`, `8`, `9`, `12`
       */
      span: number
    }
  }

  /**
   * Information needed to create a {@link Page}
   */
  export type Input = Omit<Page, 'id'>

  /**
   * Information used to update a {@link Page}
   */
  export type Updates = Partial<Input>

  /**
   * Contains the generated {@link Page} layout and its associated
   * {@link Widget} content from an AI creation request
   */
  export interface AiResult {
    /**
     * The generated {@link Page} properties including name, alias, and the
     * {@link Page.Content} structure with layout and widget references
     */
    page: Pick<Page, 'name' | 'alias' | 'content'>
    /**
     * Specifications for all {@link Widget widgets} referenced in the generated
     * {@link Page.Content}, keyed by their alias. Each spec defines the widget's
     * type, optional chart subtype, and descriptive metadata (purpose and context)
     * used to guide the AI in generating appropriate widget content
     */
    widgetSpecs: AiResult.WidgetSpec[]
  }
  export namespace AiResult {
    /**
     * Specification for a {@link Widget} to be generated by AI, containing
     * metadata that guides the AI in creating appropriate widget content
     */
    export interface WidgetSpec {
      alias: string
      type: Widget.Type.SupportsAiGeneration
      chartSubtype?: string
      /**
       * Describes the primary objective or function of this widget within
       * the page context
       *
       * @example "Display total annual revenue as key KPI"
       * @example "Show monthly revenue trend over time"
       */
      purpose: string
      /**
       * Provides additional details about the widget's data source, scope, or
       * implementation specifics to guide AI generation
       *
       * @example "Based on Sales_2024.Revenue column showing $45K-$52K range with steady growth"
       * @example "Line chart using Sales_2024.Date and Sales_2024.Revenue columns comparing 2024 performance"
       */
      context: string
    }
    export namespace WidgetSpec {
      export function getAiPrompt(
        params: Pick<WidgetSpec, 'context' | 'purpose'>,
        data: string,
      ): string {
        return `##Widget Purpose: ${params.purpose}

##Additional Context: ${params.context}
Use this context alongside the data below to better understand the data characteristics and requirements.

##Available Data:
${data}`
      }
    }
  }
}
